/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useMemo } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Icon, IllustratedMessage } from '@adobe-commerce/elsie/components';
import { AddressBook, EmptyBox } from '@adobe-commerce/elsie/icons';
import '@/account/components/EmptyList/EmptyList.css';
import { EmptyListProps } from '@/account/types';

export const EmptyList: FunctionComponent<EmptyListProps> = ({
  typeList,
  isEmpty,
  minifiedView,
  className,
}) => {
  const minifiedViewKey = minifiedView ? 'minifiedView' : 'fullSizeView';

  const translations = useText({
    addressesMessage: `Account.${minifiedViewKey}.EmptyList.Addresses.message`,
    ordersListMessage: `Account.${minifiedViewKey}.EmptyList.OrdersList.message`,
  });

  const config = useMemo(() => {
    switch (typeList) {
      case 'address':
        return {
          icon: AddressBook,
          text: <p>{translations.addressesMessage}</p>,
        };
      case 'orders':
        return {
          icon: EmptyBox,
          text: <p>{translations.ordersListMessage}</p>,
        };
      default:
        return { icon: '', text: '' };
    }
  }, [typeList, translations]);

  if (!isEmpty || !typeList || !config.text) return null;

  return (
    <IllustratedMessage
      className={classes([
        'account-empty-list',
        minifiedView ? 'account-empty-list--minified' : '',
        className,
      ])}
      message={config.text as VNode<HTMLAttributes<HTMLElement>>}
      icon={<Icon source={config.icon} />}
      data-testid="emptyList"
    />
  );
};
